import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard } from './_ui';
import * as api from '../api/platform-backup.api.js';

const STATUS_TONE = { COMPLETED: 'green', RUNNING: 'blue', PENDING: 'amber', FAILED: 'red' };
const TYPE_TONE   = { MANUAL: 'purple', SCHEDULED: 'blue' };

const fmt = (d) => d
  ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const fmtBytes = (b) => {
  if (b == null) return '—';
  const n = Number(b);
  if (n >= 1073741824) return (n / 1073741824).toFixed(2) + ' GB';
  if (n >= 1048576)    return (n / 1048576).toFixed(1) + ' MB';
  if (n >= 1024)       return (n / 1024).toFixed(0) + ' KB';
  return n + ' B';
};

const fmtDuration = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000)  return ms + 'ms';
  return (ms / 1000).toFixed(1) + 's';
};

const relAgo = (d) => {
  if (!d) return '—';
  const diffMs  = Date.now() - new Date(d).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)  return 'Just now';
  if (diffMin < 60) return diffMin + 'm ago';
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return diffH + 'h ago';
  const diffD = Math.floor(diffH / 24);
  return diffD === 1 ? 'Yesterday' : diffD + ' days ago';
};

const POLL_INTERVAL_MS = 4000;

// ── Custom hook — data loading + polling ──────────────────────────────────────

function useBackupData() {
  const toast                    = useToast();
  const [jobs, setJobs]          = useState([]);
  const [config, setConfig]      = useState(null);
  const [loading, setLoading]    = useState(true);
  const pollRef                  = useRef(null);

  const loadJobs = useCallback((quiet = false) =>
    api.listJobs()
      .then(data => setJobs(Array.isArray(data) ? data : []))
      .catch(() => { if (!quiet) toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load backup history' }); })
  , [toast]);

  const loadConfig = useCallback(() =>
    api.getConfig().then(setConfig).catch(() => {})
  , []);

  const refresh = useCallback(() => {
    setLoading(true);
    return Promise.all([loadJobs(), loadConfig()]).then(() => setLoading(false));
  }, [loadJobs, loadConfig]);

  useEffect(() => { refresh().catch(() => {}); }, [refresh]);

  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'PENDING' || j.status === 'RUNNING');
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => { loadJobs(true).catch(() => {}); }, POLL_INTERVAL_MS);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobs, loadJobs]);

  return { jobs, setJobs, config, loading, refresh };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ConfigSummary({ config }) {
  const navigate = useNavigate();
  const rows = config ? [
    { label: 'Storage',     value: 'Local' },
    { label: 'Retention',   value: config.retentionDays == null ? '—' : `${config.retentionDays} days` },
    { label: 'Compression', value: 'gzip level 6' },
    { label: 'Schedule',    value: config.schedule ?? '—' },
    { label: 'Directory',   value: config.backupDir ?? '—' },
  ] : [];

  return (
    <div className="nx-cc-chart-card">
      <div className="nx-cc-chart-head">
        <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NexusIcon name="Settings" size={14} />Configuration
        </span>
        <Pill tone="gray">Local Storage</Pill>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 12 }}>
        {rows.length > 0 ? rows.map(r => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 13, borderBottom: '1px solid var(--nx-divider)', padding: '10px 0', gap: 12 }}>
            <span className="nx-muted" style={{ flexShrink: 0 }}>{r.label}</span>
            <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-all', fontSize: 12 }}>{r.value}</span>
          </div>
        )) : (
          <div className="nx-muted" style={{ fontSize: 13, padding: '8px 0' }}>Configuration unavailable</div>
        )}
      </div>
      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--nx-border)' }}>
        <Btn variant="secondary" icon="Settings" onClick={() => navigate('/platform/settings?tab=backup')} style={{ width: '100%' }}>
          Open Backup Settings
        </Btn>
      </div>
    </div>
  );
}

function StatusPanel({ runningJob, lastCompleted, triggering, onTrigger }) {
  return (
    <div className="nx-cc-chart-card">
      <div className="nx-cc-chart-head">
        <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NexusIcon name="Activity" size={14} />Current Status
        </span>
      </div>
      {runningJob && <RunningJobStatus job={runningJob} />}
      {!runningJob && lastCompleted && <LastCompletedStatus job={lastCompleted} />}
      {!runningJob && !lastCompleted && <NoBackupPrompt triggering={triggering} onTrigger={onTrigger} />}
    </div>
  );
}

function RunningJobStatus({ job }) {
  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'var(--nx-accent-tint)', borderRadius: 10 }}>
        <NexusIcon name="Loader2" size={20} style={{ color: 'var(--nx-accent)', animation: 'spin 1s linear infinite' }} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Backup in progress</div>
          <div className="nx-muted nx-mini">Started {relAgo(job.startedAt)} · {job.triggeredBy}</div>
        </div>
        <Pill tone={STATUS_TONE[job.status] || 'gray'} style={{ marginLeft: 'auto' }}>{job.status}</Pill>
      </div>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', padding: '0 4px' }}>
        The backup is running in the background. This page will update automatically when it completes.
      </div>
    </div>
  );
}

function LastCompletedStatus({ job }) {
  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ padding: '12px 14px', borderRadius: 8, border: '1.5px solid var(--nx-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <NexusIcon name="CheckCircle2" size={18} style={{ color: 'var(--nx-success, #0E8C5A)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Last backup completed</div>
          <div className="nx-muted nx-mini">{fmt(job.startedAt)} · {fmtBytes(job.sizeBytes)} · {fmtDuration(job.durationMs)}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', padding: '0 4px' }}>
        Platform is protected. Trigger a manual backup at any time or configure automated scheduling via the <code>BACKUP_CRON</code> environment variable.
      </div>
    </div>
  );
}

function NoBackupPrompt({ triggering, onTrigger }) {
  return (
    <div className="nx-empty" style={{ padding: '32px 0', flexDirection: 'column', gap: 10 }}>
      <NexusIcon name="ShieldAlert" size={28} style={{ color: 'var(--nx-text-faint)' }} />
      <span>No backups yet. Trigger the first backup to protect platform data.</span>
      <Btn variant="primary" icon="HardDrive" size="sm" onClick={onTrigger} disabled={triggering}>
        {triggering ? 'Queuing…' : 'Run First Backup'}
      </Btn>
    </div>
  );
}

function JobRow({ job, onDownload, onRestore }) {
  const isActive = job.status === 'RUNNING' || job.status === 'PENDING';
  return (
    <tr>
      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmt(job.startedAt)}</td>
      <td><Pill tone={TYPE_TONE[job.type] || 'gray'}>{job.type?.toLowerCase()}</Pill></td>
      <td><Pill tone={STATUS_TONE[job.status] || 'gray'}>{job.status?.toLowerCase()}</Pill></td>
      <td style={{ fontSize: 12 }}>{fmtBytes(job.sizeBytes)}</td>
      <td style={{ fontSize: 12 }}>{fmtDuration(job.durationMs)}</td>
      <td style={{ fontSize: 12 }}>{job.triggeredBy || '—'}</td>
      <td style={{ fontSize: 11, color: 'var(--nx-danger)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {job.errorMsg || '—'}
      </td>
      <td style={{ textAlign: 'right' }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          {job.status === 'COMPLETED' && (
            <>
              <button className="nx-btn nx-btn-ghost nx-btn-sm" title="Download archive" style={{ padding: '3px 7px' }} onClick={() => onDownload(job)}>
                <NexusIcon name="Download" size={13} />
              </button>
              <button className="nx-btn nx-btn-ghost nx-btn-sm" title="Restore from this backup" style={{ padding: '3px 7px' }} onClick={() => onRestore(job)}>
                <NexusIcon name="RotateCcw" size={13} />
              </button>
            </>
          )}
          {isActive && (
            <NexusIcon name="Loader2" size={14} style={{ color: 'var(--nx-accent)', animation: 'spin 1s linear infinite' }} />
          )}
        </div>
      </td>
    </tr>
  );
}

function JobsTable({ jobs, onDownload, onRestore }) {
  if (jobs.length === 0) {
    return (
      <div className="nx-empty" style={{ padding: '40px 0' }}>
        <NexusIcon name="Archive" size={24} style={{ color: 'var(--nx-text-faint)' }} />
        <span>No backup jobs yet.</span>
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table className="nx-table">
        <thead>
          <tr>
            <th>Started</th><th>Type</th><th>Status</th><th>Size</th>
            <th>Duration</th><th>Triggered By</th><th>Error</th>
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map(job => (
            <JobRow key={job.id} job={job} onDownload={onDownload} onRestore={onRestore} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RestoreModal({ job, restoring, onConfirm, onClose }) {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !restoring) onClose();
  }, [restoring, onClose]);

  return (
    <dialog
      open
      style={{
        position: 'fixed', inset: 0, zIndex: 300, margin: 0, padding: 0,
        border: 'none', background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh',
      }}
      aria-labelledby="restore-dialog-title"
      onKeyDown={handleKeyDown}
    >
      <div className="nx-confirm">
        <div className="nx-confirm-icon is-danger"><NexusIcon name="RotateCcw" size={18} /></div>
        <h3 id="restore-dialog-title" className="nx-confirm-title">Restore Platform?</h3>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', textAlign: 'center', margin: '0 0 8px' }}>
          This will restore the platform to the backup taken on:
        </p>
        <p style={{ fontSize: 13, fontWeight: 700, textAlign: 'center', margin: '0 0 8px' }}>
          {fmt(job.startedAt)}
        </p>
        <p style={{ fontSize: 13, color: 'var(--nx-danger)', textAlign: 'center', margin: '0 0 20px', lineHeight: 1.5 }}>
          All data created after that point will be lost. This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Btn variant="ghost" onClick={onClose} disabled={restoring}>Cancel</Btn>
          <Btn variant="danger" icon={restoring ? 'Loader2' : 'RotateCcw'} onClick={onConfirm} disabled={restoring}>
            {restoring ? 'Initiating…' : 'Restore Now'}
          </Btn>
        </div>
      </div>
    </dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BackupRestore() {
  const { jobs, setJobs, config, loading, refresh } = useBackupData();
  const toast                     = useToast();
  const [triggering, setTriggering] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring]   = useState(false);

  const handleTrigger = useCallback(() => {
    setTriggering(true);
    api.triggerBackup()
      .then(job => {
        setJobs(prev => [job, ...prev]);
        toast({ tone: 'success', icon: 'Check', title: 'Backup started', desc: 'Backup is running in the background.' });
      })
      .catch(e => { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to trigger backup', desc: e?.message }); })
      .finally(() => setTriggering(false));
  }, [setJobs, toast]);

  const handleRestoreConfirm = useCallback(() => {
    if (!restoreTarget) return;
    setRestoring(true);
    api.restoreFromJob(restoreTarget.id)
      .then(() => {
        toast({ tone: 'success', icon: 'Check', title: 'Restore initiated', desc: 'Restore is running. Platform will reload when complete.' });
        setRestoreTarget(null);
      })
      .catch(e => { toast({ tone: 'error', icon: 'AlertCircle', title: 'Restore failed', desc: e?.message }); })
      .finally(() => setRestoring(false));
  }, [restoreTarget, toast]);

  const handleDownload = useCallback((job) => {
    const a = document.createElement('a');
    a.href = api.downloadUrl(job.id);
    a.download = `platform-backup-${job.startedAt?.slice(0, 10) ?? 'unknown'}.sql.gz`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  const lastCompleted = jobs.find(j => j.status === 'COMPLETED') ?? null;
  const runningJob    = jobs.find(j => j.status === 'RUNNING' || j.status === 'PENDING') ?? null;
  const successCount  = jobs.filter(j => j.status === 'COMPLETED').length;
  const failCount     = jobs.filter(j => j.status === 'FAILED').length;

  const lastValueColor = lastCompleted ? '#0E8C5A' : '#888';
  const jobCountColor  = failCount > 0 ? '#D97706' : '#0E8C5A';
  const jobCountSub    = failCount > 0 ? `${failCount} failed` : 'no failures';
  const jobSuffix      = jobs.length === 1 ? '' : 's';

  let triggerLabel = 'Manual Backup';
  if (triggering)    triggerLabel = 'Queuing…';
  else if (runningJob) triggerLabel = 'Backup running…';

  const triggerIcon    = triggering || runningJob ? 'Loader2' : 'HardDrive';
  const triggerDisabled = triggering || !!runningJob;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Backup &amp; Restore"
        sub="Manage platform data backups and restore points"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="RefreshCw" onClick={refresh} disabled={loading}>Refresh</Btn>
            <Btn variant="primary" icon={triggerIcon} onClick={handleTrigger} disabled={triggerDisabled}>
              {triggerLabel}
            </Btn>
          </div>
        }
      />

      {loading ? (
        <div className="nx-cc-skeleton" style={{ height: 380, marginTop: 16 }} />
      ) : (
        <>
          <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginTop: 16 }}>
            <StatCard
              label="Last Backup"
              value={lastCompleted ? relAgo(lastCompleted.startedAt) : 'Never'}
              sub={lastCompleted ? fmt(lastCompleted.startedAt) : 'No completed backups yet'}
              icon="CheckCircle2"
              color={lastValueColor}
            />
            <StatCard
              label="Backup Size"
              value={lastCompleted ? fmtBytes(lastCompleted.sizeBytes) : '—'}
              sub={lastCompleted ? 'last completed backup' : '—'}
              icon="HardDrive"
              color="#125AB0"
            />
            <StatCard
              label="Retention"
              value={config ? `${config.retentionDays} days` : '—'}
              sub="configured window"
              icon="Calendar"
              color="#125AB0"
            />
            <StatCard label="Jobs" value={successCount} sub={jobCountSub} icon="Archive" color={jobCountColor} />
          </div>

          <div className="nx-cc-grid2" style={{ marginTop: 16 }}>
            <ConfigSummary config={config} />
            <StatusPanel runningJob={runningJob} lastCompleted={lastCompleted} triggering={triggering} onTrigger={handleTrigger} />
          </div>

          <div className="nx-cc-chart-card" style={{ marginTop: 16 }}>
            <div className="nx-cc-chart-head">
              <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <NexusIcon name="History" size={14} />Backup History
              </span>
              <span className="nx-muted nx-mini">{jobs.length} job{jobSuffix}</span>
            </div>
            <JobsTable jobs={jobs} onDownload={handleDownload} onRestore={setRestoreTarget} />
          </div>
        </>
      )}

      {restoreTarget && (
        <RestoreModal
          job={restoreTarget}
          restoring={restoring}
          onConfirm={handleRestoreConfirm}
          onClose={() => setRestoreTarget(null)}
        />
      )}
    </div>
  );
}
